import {
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  MessageSquare,
  ChevronsUpDown,
  Plus,
  Trash2,
  MoreHorizontal,
  Pin,
  PinOff,
  Pencil,
  Rocket,
} from 'lucide-react'
import { Link, useLocation, useNavigate, useSearch } from '@tanstack/react-router'
import { forwardRef, useState, useEffect, useCallback, useRef } from 'react'
import type { ComponentProps, MouseEvent as ReactMouseEvent } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ProjectIcon } from '@/components/project-icon'
import { ProjectSwitcher } from '@/components/project-switcher'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import { useDateLocale } from '@/i18n/dates'
import { navigation, bottomNavigation, settingsNavItem } from '@/lib/navigation'
import { UsageBar } from '@/components/sidebar/usage-bar'
import { useAnimatedNavIcon } from '@/components/animated-nav-icon'
import { usePresence, useProjectEvent } from '@/lib/collab/presence-context'
import { useOptionalProject } from '@/lib/project-context'
import { parseSettingsSection } from '@/lib/settings-dialog'
import { useSettingsDialog } from '@/components/settings/use-settings-dialog'
import { resolveLandingProjectId, useLastProjectId } from '@/lib/last-project'
import { useAuthState } from '@/lib/auth-state'
import { orpc } from '@/lib/orpc-client'
import { CreateProjectDialog } from '@/components/create-project-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import type { NavItem, GlobalNavItem } from '@/lib/navigation'
import { forgetChat, warmChat } from '@/lib/chat/chat-pool'
import { isOrgGuest } from '@hub/contracts'

const AnimatedNavLink = forwardRef<
  HTMLAnchorElement,
  {
    item: NavItem
    isActive: boolean
    isCollapsed: boolean
    projectId: string
  }
>(function AnimatedNavLink({ item, isActive, isCollapsed, projectId, ...rest }, ref) {
  const { hasAnimated, iconElement, onMouseEnter, onMouseLeave } = useAnimatedNavIcon(
    item.segment,
    16
  )
  const { t } = useTranslation()

  return (
    <Link
      ref={ref}
      to={`/$projectId/${item.segment}` as string}
      params={{ projectId }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
        'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        isActive &&
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
        isCollapsed && 'justify-center'
      )}
      {...rest}
    >
      {hasAnimated ? iconElement : <item.icon className="h-4 w-4 flex-shrink-0" />}
      {!isCollapsed && t(`nav.${item.key}`)}
    </Link>
  )
})

/**
 * The gear opens the settings dialog (board h39z) on top of the current page:
 * `?settings=menu` is added to the URL, other search params stay. Outside a
 * project it lands on the project's chat with the dialog open.
 */
const AnimatedSettingsLink = forwardRef<
  HTMLAnchorElement,
  {
    item: NavItem
    isActive: boolean
    isCollapsed: boolean
    projectId: string
    inProject: boolean
  } & Pick<ComponentProps<'a'>, 'onClick'>
>(function AnimatedSettingsLink(
  { item, isActive, isCollapsed, projectId, inProject, onClick, ...rest },
  ref
) {
  const { hasAnimated, iconElement, onMouseEnter, onMouseLeave } = useAnimatedNavIcon(
    item.segment,
    16
  )
  const { t } = useTranslation()
  const settingsDialog = useSettingsDialog()

  const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (!inProject || event.defaultPrevented) return
    // A plain click opens the dialog through the one hook that owns its history;
    // modified clicks (new tab/window) follow the href.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return
    }
    event.preventDefault()
    settingsDialog.open('menu')
  }

  return (
    <Link
      ref={ref}
      {...(inProject
        ? {
            to: '.',
            search: ((prev: Record<string, unknown>) => ({ ...prev, settings: 'menu' })) as never,
          }
        : {
            to: '/$projectId/chat',
            params: { projectId },
            search: { settings: 'menu' as const },
          })}
      onClick={handleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors',
        isActive &&
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
        isCollapsed && 'justify-center'
      )}
      {...rest}
    >
      {hasAnimated ? iconElement : <item.icon className="h-4 w-4 flex-shrink-0" />}
      {!isCollapsed && t(`nav.${item.key}`)}
    </Link>
  )
})

const AnimatedGlobalNavLink = forwardRef<
  HTMLAnchorElement,
  {
    item: GlobalNavItem
    isActive: boolean
    isCollapsed: boolean
  }
>(function AnimatedGlobalNavLink({ item, isActive, isCollapsed, ...rest }, ref) {
  const segment = item.to.replace(/^\//, '')
  const { hasAnimated, iconElement, onMouseEnter, onMouseLeave } = useAnimatedNavIcon(segment, 16)
  const { t } = useTranslation()

  return (
    <Link
      ref={ref}
      to={item.to}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors',
        isActive &&
          'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
        isCollapsed && 'justify-center'
      )}
      {...rest}
    >
      {hasAnimated ? iconElement : <item.icon className="h-4 w-4 flex-shrink-0" />}
      {!isCollapsed && t(`nav.${item.key}`)}
    </Link>
  )
})

type ProjectGroup = Awaited<ReturnType<typeof orpc.projects.listAllForMe>>['groups'][number]
type ChatThread = Awaited<ReturnType<typeof orpc.chat.listThreads>>['items'][number]

interface SidebarProps {
  /** 'rail' (default): collapsible desktop rail. 'drawer': fills a mobile sheet, never collapsed. */
  variant?: 'rail' | 'drawer'
  className?: string
}

export function Sidebar({ variant = 'rail', className }: SidebarProps = {}) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { dateFns } = useDateLocale()
  const { me } = useAuthState()
  const projectCtx = useOptionalProject()
  // Outside the project scope (/settings) keep the project the user came
  // from: the local hint is fresher than `me.project`, which was resolved when
  // the page loaded. Both are validated against the membership list below.
  const lastProjectId = useLastProjectId(me?.user.id)
  const memberProjectIds = me?.memberships.projects.map((p) => p.projectId) ?? []
  const projectId =
    projectCtx?.projectId ??
    resolveLandingProjectId({
      serverProjectId: me?.project?.id,
      memberProjectIds,
      storedProjectId: lastProjectId,
    }) ??
    undefined
  const [projectGroups, setProjectGroups] = useState<ProjectGroup[]>([])
  const [chatThreads, setChatThreads] = useState<ChatThread[]>([])
  const { activeThreads } = usePresence()
  const [isChatsOpen, setIsChatsOpen] = useState(true)
  const [isCollapsedState, setIsCollapsed] = useState(false)
  const isCollapsed = variant === 'rail' && isCollapsedState
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<ChatThread | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [renameTarget, setRenameTarget] = useState<ChatThread | null>(null)
  const [renameTitle, setRenameTitle] = useState('')
  const [isRenaming, setIsRenaming] = useState(false)
  const [taskCount, setTaskCount] = useState(0)
  const [inboxCount, setInboxCount] = useState(0)
  // PLAN-084: the inbox is an organization feature; off = no nav item, no badge.
  const inboxEnabled = projectCtx?.project.features.inbox ?? false

  const loadProjects = useCallback(() => {
    orpc.projects
      .listAllForMe({})
      .then((res) => setProjectGroups(res.groups))
      .catch(() => {})
  }, [])

  useEffect(() => {
    loadProjects()
  }, [loadProjects])

  useEffect(() => {
    const handler = () => loadProjects()
    window.addEventListener('project-list-changed', handler)
    return () => window.removeEventListener('project-list-changed', handler)
  }, [loadProjects])

  const loadThreads = useCallback(() => {
    if (!projectId) return
    orpc.chat
      .listThreads({ projectId })
      .then((res) => setChatThreads(res.items))
      .catch(() => {})
  }, [projectId])

  useEffect(() => {
    loadThreads()
  }, [loadThreads])

  useEffect(() => {
    const handler = () => loadThreads()
    window.addEventListener('chat-threads-changed', handler)
    return () => window.removeEventListener('chat-threads-changed', handler)
  }, [loadThreads])

  // Task count for sidebar badge
  const loadTaskCount = useCallback(() => {
    if (!projectId) return
    orpc.tasks
      .myCount({ projectId })
      .then((res) => setTaskCount(res.count))
      .catch(() => {})
  }, [projectId])

  useEffect(() => {
    loadTaskCount()
  }, [loadTaskCount])

  useEffect(() => {
    const handler = () => loadTaskCount()
    window.addEventListener('tasks-changed', handler)
    return () => window.removeEventListener('tasks-changed', handler)
  }, [loadTaskCount])

  // Inbox badge (PLAN-084): emails that still need eyes; refetched on the
  // project's `inbox:changed` event.
  const loadInboxCount = useCallback(() => {
    if (!projectId || !inboxEnabled) return
    orpc.inbox
      .badgeCount({ projectId })
      .then((res) => setInboxCount(res.count))
      .catch(() => {})
  }, [projectId, inboxEnabled])

  useEffect(() => {
    loadInboxCount()
  }, [loadInboxCount])

  useProjectEvent(['inbox:changed'], loadInboxCount)

  // PLAN-085: which HUB video meetings are live right now. The set is
  // rebuilt from `meeting:live` events plus one read on mount, so a member
  // who opens HUB mid-meeting still sees the dot.
  const [liveMeetings, setLiveMeetings] = useState<ReadonlySet<string>>(() => new Set())
  useEffect(() => {
    if (!projectId) return
    let cancelled = false
    orpc.meetings
      .list({ projectId })
      .then((res) => {
        if (cancelled) return
        setLiveMeetings(new Set(res.items.filter((m) => m.roomStatus === 'live').map((m) => m.id)))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [projectId])

  useProjectEvent(['meeting:live'], (event) => {
    if (event.t !== 'meeting:live') return
    setLiveMeetings((current) => {
      const next = new Set(current)
      if (event.live) next.add(event.meetingId)
      else next.delete(event.meetingId)
      return next
    })
  })

  // Pre-open the chat room after a short hover so the socket is synced by
  // the time the user clicks (pass-through hovers don't trigger it).
  const warmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cancelWarm = useCallback(() => {
    if (warmTimerRef.current) clearTimeout(warmTimerRef.current)
    warmTimerRef.current = null
  }, [])
  const scheduleWarm = useCallback(
    (threadId: string) => {
      if (!projectId) return
      cancelWarm()
      warmTimerRef.current = setTimeout(() => {
        warmTimerRef.current = null
        warmChat(projectId, threadId)
      }, 150)
    },
    [projectId, cancelWarm]
  )

  const handleDeleteThread = async () => {
    if (!deleteTarget || !projectId || isDeleting) return
    setIsDeleting(true)
    try {
      await orpc.chat.deleteThread({ projectId, threadId: deleteTarget.id })
      void forgetChat(projectId, deleteTarget.id)
      setDeleteTarget(null)
      window.dispatchEvent(new CustomEvent('chat-threads-changed'))
      // Navigate away if currently viewing the deleted thread
      if (pathname.includes(deleteTarget.id)) {
        void navigate({ to: '/$projectId/chat', params: { projectId } })
      }
    } catch {
      /* ignore */
    } finally {
      setIsDeleting(false)
    }
  }

  const handlePinThread = async (thread: ChatThread) => {
    if (!projectId) return
    try {
      await orpc.chat.updateThread({
        projectId,
        threadId: thread.id,
        pinned: !thread.pinned,
      })
      window.dispatchEvent(new CustomEvent('chat-threads-changed'))
    } catch {
      /* ignore */
    }
  }

  const handleRenameThread = async () => {
    if (!renameTarget || !projectId || isRenaming || !renameTitle.trim()) return
    setIsRenaming(true)
    try {
      await orpc.chat.updateThread({
        projectId,
        threadId: renameTarget.id,
        title: renameTitle.trim(),
      })
      setRenameTarget(null)
      window.dispatchEvent(new CustomEvent('chat-threads-changed'))
    } catch {
      /* ignore */
    } finally {
      setIsRenaming(false)
    }
  }

  const currentProject = projectGroups.flatMap((g) => g.projects).find((p) => p.id === projectId)
  const currentGroup = projectGroups.find((g) => g.projects.some((p) => p.id === projectId))
  const projectDisplayName = currentProject?.name ?? t('sidebar.projectFallback')
  const orgDisplayName = currentGroup?.organization.name ?? me?.organization?.name ?? ''

  // Guests cannot create projects — but an owner/admin is never a guest (b0hx)
  const currentOrgId = currentGroup?.organization.id ?? me?.organization?.id
  const currentOrgMembership = me?.memberships.organizations.find(
    (m) => m.organizationId === currentOrgId
  )
  const isGuestInCurrentOrg = currentOrgMembership ? isOrgGuest(currentOrgMembership) : false

  const handleProjectSwitch = (newProjectId: string) => {
    void navigate({ to: '/$projectId/chat', params: { projectId: newProjectId } })
  }
  const switcherProps = {
    groups: projectGroups,
    currentProjectId: projectId,
    onSelect: handleProjectSwitch,
    inDrawer: variant === 'drawer',
  }

  const activeSegment = pathname.split('/').filter(Boolean)[1] ?? ''
  // The settings dialog is a search param on top of any page (board h39z).
  const settingsOpen = !!parseSettingsSection(
    (useSearch({ strict: false }) as Record<string, unknown>).settings
  )

  return (
    <aside
      className={cn(
        'border-r border-border bg-sidebar flex flex-col transition-all duration-300',
        variant === 'drawer' ? 'w-full border-r-0' : isCollapsed ? 'w-16' : 'w-64',
        className
      )}
    >
      {/* Header */}
      <div className="px-4 border-b border-sidebar-border flex items-center justify-between h-[65px]">
        {!isCollapsed && (
          <Link to="/$projectId" params={{ projectId: projectId! }}>
            <h1 className="text-xl font-bold text-sidebar-foreground cursor-pointer">HUB</h1>
          </Link>
        )}
        <div className="flex items-center gap-2">
          {variant === 'rail' && (
            <TooltipProvider delayDuration={500}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    className="h-8 w-8"
                  >
                    {isCollapsed ? (
                      <PanelLeftOpen className="h-4 w-4" />
                    ) : (
                      <PanelLeftClose className="h-4 w-4" />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {isCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Project Selector */}
      {isCollapsed && (
        <div className="px-3 pt-3 flex justify-center">
          <ProjectSwitcher
            {...switcherProps}
            tooltip={`${projectDisplayName} — ${orgDisplayName}`}
            trigger={
              <button className="flex items-center justify-center rounded-md hover:opacity-80 transition-opacity">
                <ProjectIcon
                  name={projectDisplayName}
                  icon={currentProject?.icon ?? null}
                  avatarUrl={currentProject?.avatarUrl ?? null}
                  size="md"
                />
              </button>
            }
          />
        </div>
      )}
      {!isCollapsed && (
        <div className="px-3 pt-3">
          <ProjectSwitcher
            {...switcherProps}
            createProject={
              currentGroup && !isGuestInCurrentOrg
                ? {
                    count: currentGroup.projectCount,
                    limit: currentGroup.projectLimit,
                    onCreate: () => setIsCreateDialogOpen(true),
                  }
                : undefined
            }
            trigger={
              <Button
                variant="outline"
                className="w-full justify-between bg-transparent text-sidebar-foreground border-sidebar-border hover:bg-sidebar-accent h-auto py-2"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <ProjectIcon
                    name={projectDisplayName}
                    icon={currentProject?.icon ?? null}
                    avatarUrl={currentProject?.avatarUrl ?? null}
                    size="md"
                  />
                  <span className="flex flex-col items-start min-w-0">
                    <span className="text-[11px] leading-tight text-sidebar-foreground/60 truncate max-w-full">
                      {orgDisplayName}
                    </span>
                    <span className="text-sm font-semibold leading-tight truncate max-w-full">
                      {projectDisplayName}
                    </span>
                  </span>
                </span>
                <ChevronsUpDown className="h-4 w-4 flex-shrink-0 opacity-50" />
              </Button>
            }
          />
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        <TooltipProvider delayDuration={500}>
          {projectId &&
            navigation
              .filter((item) => item.key !== 'inbox' || inboxEnabled)
              .map((item) => {
                const isActive = activeSegment === item.segment
                const badgeCount =
                  item.segment === 'tasks' ? taskCount : item.segment === 'inbox' ? inboxCount : 0
                const showBadge = badgeCount > 0
                // PLAN-085: a green dot on the calendar while a HUB video
                // meeting is running somewhere in the project.
                const showLiveDot = item.segment === 'calendar' && liveMeetings.size > 0
                return (
                  <Tooltip key={item.key}>
                    <TooltipTrigger asChild>
                      <div className="relative">
                        <AnimatedNavLink
                          item={item}
                          isActive={isActive}
                          isCollapsed={isCollapsed}
                          projectId={projectId}
                        />
                        {showBadge && !isCollapsed && (
                          <Badge
                            variant="secondary"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] px-1.5 py-0 rounded-full min-w-[20px] text-center"
                          >
                            {badgeCount}
                          </Badge>
                        )}
                        {showBadge && isCollapsed && (
                          <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[9px] rounded-full min-w-[14px] h-[14px] flex items-center justify-center">
                            {badgeCount}
                          </span>
                        )}
                        {showLiveDot && (
                          <span
                            aria-hidden
                            className="absolute top-1/2 right-2 flex h-2 w-2 -translate-y-1/2"
                          >
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                          </span>
                        )}
                      </div>
                    </TooltipTrigger>
                    {isCollapsed && (
                      <TooltipContent side="right">{t(`nav.${item.key}`)}</TooltipContent>
                    )}
                  </Tooltip>
                )
              })}
        </TooltipProvider>

        {!isCollapsed && projectId && (
          <div className="pt-4">
            <div className="flex items-center justify-between px-3 py-2">
              <button
                onClick={() => setIsChatsOpen(!isChatsOpen)}
                className="flex items-center gap-2 text-xs font-medium text-sidebar-foreground/60 hover:text-sidebar-foreground transition-colors"
              >
                <ChevronRight
                  className={cn('h-3 w-3 transition-transform', isChatsOpen && 'rotate-90')}
                />
                {t('sidebar.chats')}
              </button>
              <Link
                to="/$projectId/chat"
                params={{ projectId }}
                className="p-1 rounded-md text-sidebar-foreground/40 hover:text-sidebar-foreground hover:bg-sidebar-accent transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
              </Link>
            </div>
            {isChatsOpen && (
              <div className="mt-1 space-y-0.5">
                {chatThreads.length === 0 && (
                  <p className="px-3 pl-8 text-xs text-sidebar-foreground/40">
                    {t('sidebar.noChats')}
                  </p>
                )}
                {(() => {
                  const pinned = chatThreads.filter((t) => t.pinned)
                  const unpinned = chatThreads.filter((t) => !t.pinned)
                  const visibleUnpinned = unpinned.slice(0, 10)
                  const hasMore = unpinned.length > 10

                  const renderThread = (thread: ChatThread) => {
                    const isThreadActive = pathname.includes(thread.id)
                    const isWorking = activeThreads.has(thread.id)
                    const ThreadIcon = thread.onboarding ? Rocket : MessageSquare
                    return (
                      <Tooltip key={thread.id}>
                        <TooltipTrigger asChild>
                          <div className="flex items-center group">
                            <Link
                              to="/$projectId/chat/$chatId"
                              params={{ projectId, chatId: thread.id }}
                              onMouseEnter={() => scheduleWarm(thread.id)}
                              onMouseLeave={cancelWarm}
                              onFocus={() => scheduleWarm(thread.id)}
                              onBlur={cancelWarm}
                              className={cn(
                                'flex items-center gap-2 px-3 py-1.5 pl-8 text-xs rounded-lg transition-colors flex-1 min-w-0',
                                isThreadActive
                                  ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                                  : 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                              )}
                            >
                              <ThreadIcon
                                data-onboarding={thread.onboarding ? '' : undefined}
                                aria-label={isWorking ? t('sidebar.agentWorking') : undefined}
                                className={cn(
                                  'h-3 w-3 flex-shrink-0 transition-colors',
                                  isWorking
                                    ? 'animate-pulse text-blue-600 drop-shadow-[0_0_4px_rgba(37,99,235,0.7)] motion-reduce:animate-none dark:text-blue-400 dark:drop-shadow-[0_0_5px_rgba(96,165,250,0.8)]'
                                    : thread.onboarding
                                      ? 'text-sidebar-foreground/70'
                                      : 'text-sidebar-foreground/40'
                                )}
                              />
                              <span className="truncate">
                                {thread.title || t('sidebar.newChat')}
                              </span>
                            </Link>
                            <DropdownMenu modal={false}>
                              <DropdownMenuTrigger asChild>
                                <button className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-sidebar-accent text-sidebar-foreground/40 hover:text-sidebar-foreground transition-all shrink-0 mr-1">
                                  <MoreHorizontal className="h-3 w-3" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-40">
                                <DropdownMenuItem onClick={() => void handlePinThread(thread)}>
                                  {thread.pinned ? (
                                    <>
                                      <PinOff className="h-3.5 w-3.5 mr-2" />
                                      {t('sidebar.unpin')}
                                    </>
                                  ) : (
                                    <>
                                      <Pin className="h-3.5 w-3.5 mr-2" />
                                      {t('sidebar.pin')}
                                    </>
                                  )}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setRenameTarget(thread)
                                    setRenameTitle(thread.title || '')
                                  }}
                                >
                                  <Pencil className="h-3.5 w-3.5 mr-2" />
                                  {t('actions.rename')}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setDeleteTarget(thread)}
                                >
                                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                                  {t('actions.delete')}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right" className="max-w-xs">
                          <p>{thread.title || t('sidebar.newChat')}</p>
                          <p className="text-muted-foreground mt-0.5 text-[11px]">
                            {t('sidebar.lastMessage', {
                              when: formatDistanceToNow(
                                new Date(thread.lastMessageAt ?? thread.updatedAt),
                                { locale: dateFns, addSuffix: true }
                              ),
                            })}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )
                  }

                  return (
                    <TooltipProvider delayDuration={500}>
                      {pinned.length > 0 && (
                        <>
                          <p className="px-3 pl-8 pt-1 pb-0.5 text-[10px] font-medium text-sidebar-foreground/40 uppercase tracking-wider">
                            {t('sidebar.pinned')}
                          </p>
                          {pinned.map(renderThread)}
                        </>
                      )}
                      {pinned.length > 0 && visibleUnpinned.length > 0 && (
                        <p className="px-3 pl-8 pt-1 pb-0.5 text-[10px] font-medium text-sidebar-foreground/40 uppercase tracking-wider">
                          {t('sidebar.recent')}
                        </p>
                      )}
                      {visibleUnpinned.map(renderThread)}
                      {hasMore && (
                        <Link
                          to="/$projectId/chats"
                          params={{ projectId }}
                          className="flex items-center gap-2 px-3 py-1.5 pl-8 text-xs text-sidebar-foreground/50 hover:text-sidebar-foreground transition-colors"
                        >
                          {t('sidebar.allChats')} &rarr;
                        </Link>
                      )}
                    </TooltipProvider>
                  )
                })()}
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Bottom Navigation */}
      <div className="p-3 border-t border-sidebar-border space-y-1">
        <TooltipProvider delayDuration={500}>
          {projectId && !isCollapsed && <UsageBar projectId={projectId} />}
          {bottomNavigation.map((item) => (
            <Tooltip key={item.key}>
              <TooltipTrigger asChild>
                <AnimatedGlobalNavLink
                  item={item}
                  isActive={pathname.startsWith(item.to)}
                  isCollapsed={isCollapsed}
                />
              </TooltipTrigger>
              {isCollapsed && <TooltipContent side="right">{t(`nav.${item.key}`)}</TooltipContent>}
            </Tooltip>
          ))}
          {projectId && (
            <Tooltip>
              <TooltipTrigger asChild>
                <AnimatedSettingsLink
                  item={settingsNavItem}
                  isActive={settingsOpen}
                  isCollapsed={isCollapsed}
                  projectId={projectId}
                  inProject={!!projectCtx}
                />
              </TooltipTrigger>
              {isCollapsed && <TooltipContent side="right">{t('nav.settings')}</TooltipContent>}
            </Tooltip>
          )}
        </TooltipProvider>
      </div>

      {/* Create Project Dialog */}
      {projectGroups.length > 0 && (
        <CreateProjectDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          projectGroups={projectGroups}
          defaultOrganizationId={currentGroup?.organization.id ?? projectGroups[0].organization.id}
        />
      )}

      {/* Rename chat thread dialog */}
      <Dialog
        open={!!renameTarget}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sidebar.renameChatTitle')}</DialogTitle>
            <DialogDescription>{t('sidebar.renameChatDescription')}</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              void handleRenameThread()
            }}
          >
            <Input
              value={renameTitle}
              onChange={(e) => setRenameTitle(e.target.value)}
              placeholder={t('sidebar.chatNamePlaceholder')}
              autoFocus
            />
            <DialogFooter className="mt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameTarget(null)}
                disabled={isRenaming}
              >
                {t('actions.cancel')}
              </Button>
              <Button type="submit" disabled={isRenaming || !renameTitle.trim()}>
                {isRenaming ? t('actions.saving') : t('actions.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete chat thread dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('sidebar.deleteChatTitle')}</DialogTitle>
            <DialogDescription>
              {t('sidebar.deleteChatConfirm', {
                title: deleteTarget?.title || t('sidebar.newChat'),
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={isDeleting}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeleteThread()}
              disabled={isDeleting}
            >
              {isDeleting ? t('sidebar.deleting') : t('actions.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </aside>
  )
}
